import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../context/AppContext';
import DashboardCard from '../components/DashboardCard';
import { useTranslation } from 'react-i18next';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

const SalesDashboard = () => {
    const { selectedDepo, authFetch, depos } = useContext(AppContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    
    // Filters
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterCashier, setFilterCashier] = useState('All');
    const [filterPayment, setFilterPayment] = useState('All');
    const [filterCustomer, setFilterCustomer] = useState('All');
    
    // Lists for dropdowns
    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);

    const [data, setData] = useState({
        summary: {
            total_sales: 0,
            cash_sales: 0,
            card_sales: 0,
            account_sales: 0,
            returns: 0,
            expenses: 0,
            net_sales: 0,
            num_invoices: 0
        },
        charts: {
            hourly_sales: [],
            payment_distribution: [],
            top_products: []
        }
    });

    // Fetch dropdowns
    useEffect(() => {
        authFetch('/api/users').then(res => res.json()).then(data => setUsers(Array.isArray(data) ? data : []));
        authFetch('/api/customers').then(res => res.json()).then(data => setCustomers(Array.isArray(data) ? data : []));
    }, [authFetch]);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams({
            date: filterDate,
            cashier: filterCashier,
            payment_type: filterPayment,
            branch: selectedDepo,
            customer: filterCustomer
        });

        authFetch(`/api/sales_dashboard?${params.toString()}`)
            .then(res => res.json())
            .then(resData => {
                if (resData.summary) {
                    setData(resData);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [selectedDepo, filterDate, filterCashier, filterPayment, filterCustomer, authFetch]);

    const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'CFA' }).format(val || 0);

    // Chart Data Configs
    const hourlyData = {
        labels: data.charts.hourly_sales.map(item => `${item.hour}:00`),
        datasets: [
            {
                label: t('Sales per Hour'),
                data: data.charts.hourly_sales.map(item => parseFloat(item.sales)),
                borderColor: 'rgb(99, 102, 241)',
                backgroundColor: 'rgba(99, 102, 241, 0.5)',
                tension: 0.3,
                fill: true
            }
        ]
    };

    const paymentData = {
        labels: data.charts.payment_distribution.map(item => item.payment_method || 'Unknown'),
        datasets: [
            {
                data: data.charts.payment_distribution.map(item => parseFloat(item.total_amount)),
                backgroundColor: [
                    '#10b981', // Green for Cash
                    '#3b82f6', // Blue for Card
                    '#f59e0b', // Amber for Account
                    '#8b5cf6', // Purple
                    '#ec4899', // Pink
                ],
                borderWidth: 0,
            }
        ]
    };

    const topProductsData = {
        labels: data.charts.top_products.map(item => item.name),
        datasets: [
            {
                label: t('Quantity Sold'),
                data: data.charts.top_products.map(item => parseInt(item.qty_sold)),
                backgroundColor: 'rgba(59, 130, 246, 0.8)',
            }
        ]
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800 tracking-tight">{t('Sales Dashboard')}</h2>
                    <p className="text-gray-500 mt-1">{t('Live summary of all sales activities')}</p>
                </div>
                
                {/* Filters */}
                <div className="grid grid-cols-2 md:flex gap-2">
                    <input 
                        type="date" 
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        className="bg-white border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-2"
                    />
                    <select 
                        value={filterCashier}
                        onChange={(e) => setFilterCashier(e.target.value)}
                        className="bg-white border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-2"
                    >
                        <option value="All">{t('All Cashiers')}</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <select 
                        value={filterPayment}
                        onChange={(e) => setFilterPayment(e.target.value)}
                        className="bg-white border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-2"
                    >
                        <option value="All">{t('All Payment Types')}</option>
                        <option value="Cash">{t('Cash')}</option>
                        <option value="Visa">{t('Card')}</option>
                        <option value="Account">{t('Account Credit')}</option>
                    </select>
                    <select 
                        value={filterCustomer}
                        onChange={(e) => setFilterCustomer(e.target.value)}
                        className="bg-white border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-2"
                    >
                        <option value="All">{t('All Customers')}</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
            </header>

            {loading ? (
                <div className="flex justify-center items-center h-64 text-indigo-600">
                    <svg className="animate-spin h-10 w-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <DashboardCard title={t('Total Sales')} value={formatCurrency(data.summary.total_sales)} icon="🧾" color="border-indigo-500 bg-white" />
                        <DashboardCard title={t('Cash Sales')} value={formatCurrency(data.summary.cash_sales)} icon="💵" color="border-green-500 bg-white" />
                        <DashboardCard title={t('Card Sales')} value={formatCurrency(data.summary.card_sales)} icon="💳" color="border-blue-500 bg-white" />
                        <DashboardCard title={t('Account Credit')} value={formatCurrency(data.summary.account_sales)} icon="📝" color="border-amber-500 bg-white" />
                        
                        <DashboardCard title={t('Returns')} value={formatCurrency(data.summary.returns)} icon="🔙" color="border-red-400 bg-white" />
                        <DashboardCard title={t('Expenses')} value={formatCurrency(data.summary.expenses)} icon="📉" color="border-orange-500 bg-white" />
                        <DashboardCard title={t('Net Sales')} value={formatCurrency(data.summary.net_sales)} icon="💎" color="border-emerald-500 bg-emerald-50" />
                        <DashboardCard title={t('Invoices')} value={data.summary.num_invoices} icon="📑" color="border-gray-500 bg-white" />
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Hourly Sales Line Chart */}
                        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm lg:col-span-2">
                            <h3 className="text-lg font-bold text-gray-800 mb-4">{t('Hourly Sales')}</h3>
                            <div className="h-64">
                                {data.charts.hourly_sales.length > 0 ? (
                                    <Line data={hourlyData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400">{t('No data')}</div>
                                )}
                            </div>
                        </div>

                        {/* Payment Type Doughnut */}
                        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-800 mb-4">{t('Payment Distribution')}</h3>
                            <div className="h-64">
                                {data.charts.payment_distribution.length > 0 ? (
                                    <Doughnut data={paymentData} options={{ maintainAspectRatio: false }} />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400">{t('No data')}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Top Selling Products Bar Chart */}
                    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">{t('Top Selling Products')}</h3>
                        <div className="h-72">
                            {data.charts.top_products.length > 0 ? (
                                <Bar data={topProductsData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400">{t('No data')}</div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SalesDashboard;
